import * as React from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from "@/components/ui/tooltip"
import { Facebook, Instagram, Linkedin, Twitter, Send, Phone, MapPin, Mail, Clock } from "lucide-react"
import { useDentoData } from "../../context/DentoContext"

function DentoFooter() {
    const { hospitalData } = useDentoData();

    return (
        <footer className="relative border-t border-gray-100 bg-white text-black font-body">
            <div className="container mx-auto px-4 py-16 md:px-6 lg:px-8">
                <div className="grid gap-12 md:grid-cols-2 lg:grid-cols-4">
                    {/* Column 1: Newsletter */}
                    <div className="relative">
                        <h2 className="mb-2 text-3xl font-extrabold tracking-tight text-black font-heading">Stay Connected</h2>
                        <p className="mb-6 text-black/50 text-sm leading-relaxed">
                            Join our newsletter for dental care tips, clinic updates, and exclusive offers.
                        </p>
                        <form className="relative" onSubmit={(e) => e.preventDefault()}>
                            <Input
                                type="email"
                                placeholder="Enter your email"
                                className="pr-12 border-gray-200 bg-gray-50 focus:ring-black"
                            />
                            <Button
                                type="submit"
                                size="icon"
                                className="absolute right-1 top-1 h-8 w-8 rounded-full bg-black text-white transition-all hover:bg-black/80"
                            >
                                <Send className="h-4 w-4" />
                                <span className="sr-only">Subscribe</span>
                            </Button>
                        </form>
                        <div className="absolute -right-4 top-0 h-24 w-24 rounded-full bg-black/5 blur-2xl" />
                    </div>

                    {/* Column 2: Quick Links */}
                    <div>
                        <h3 className="mb-6 text-sm font-bold text-black uppercase tracking-widest font-heading">Quick Links</h3>
                        <nav className="space-y-3 text-sm">
                            {[
                                { name: 'Home', href: '#home' },
                                { name: 'About Us', href: '#about' },
                                { name: 'Our Services', href: '#services' },
                                { name: 'Meet Dr. Ajinkya', href: '#doctor' },
                                { name: 'Patient Reviews', href: '#testimonials' },
                                { name: 'Clinic Gallery', href: '#gallery' },
                            ].map((link) => (
                                <a key={link.name} href={link.href} className="block text-black/60 transition-colors hover:text-black font-medium">
                                    {link.name}
                                </a>
                            ))}
                        </nav>
                    </div>

                    {/* Column 3: Contact Info */}
                    <div>
                        <h3 className="mb-6 text-sm font-bold text-black uppercase tracking-widest font-heading">Visit Us</h3>
                        <div className="space-y-4 text-sm text-black/60">
                            <div className="flex gap-3">
                                <MapPin className="shrink-0 text-black mt-0.5" size={16} />
                                <span className="leading-relaxed">{hospitalData.address}</span>
                            </div>
                            <div className="flex gap-3">
                                <Phone className="shrink-0 text-black mt-0.5" size={16} />
                                <a href={`tel:${hospitalData.phone}`} className="hover:text-black transition-colors">{hospitalData.phone}</a>
                            </div>
                            <div className="flex gap-3">
                                <Mail className="shrink-0 text-black mt-0.5" size={16} />
                                <a href={`mailto:${hospitalData.email}`} className="hover:text-black transition-colors">{hospitalData.email}</a>
                            </div>
                            <div className="flex gap-3">
                                <Clock className="shrink-0 text-black mt-0.5" size={16} />
                                <div>
                                    <p>Mon - Sat: 10:00 AM - 9:00 PM</p>
                                    <p>Sunday: By Appointment</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Column 4: Social */}
                    <div className="relative">
                        <h3 className="mb-6 text-sm font-bold text-black uppercase tracking-widest font-heading">Follow Us</h3>
                        <div className="mb-8 flex space-x-3">
                            <TooltipProvider>
                                {[
                                    { icon: Facebook, label: 'Facebook' },
                                    { icon: Twitter, label: 'Twitter' },
                                    { icon: Instagram, label: 'Instagram' },
                                    { icon: Linkedin, label: 'LinkedIn' },
                                ].map(({ icon: Icon, label }) => (
                                    <Tooltip key={label}>
                                        <TooltipTrigger asChild>
                                            <Button variant="outline" size="icon" className="rounded-full border-gray-200 hover:bg-black hover:text-white hover:border-black transition-all">
                                                <Icon className="h-4 w-4" />
                                                <span className="sr-only">{label}</span>
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Follow us on {label}</p>
                                        </TooltipContent>
                                    </Tooltip>
                                ))}
                            </TooltipProvider>
                        </div>

                        {/* Emergency Call Box */}
                        <div className="bg-black text-white p-6 rounded-3xl shadow-xl shadow-gray-200/50 border border-white/10">
                            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/40 mb-1 font-body">Emergency</p>
                            <a href={`tel:${hospitalData.emergency}`} className="text-xl font-extrabold hover:text-white/80 transition-colors font-heading tracking-tight">
                                {hospitalData.emergency}
                            </a>
                        </div>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="mt-12 flex flex-col items-center justify-between gap-4 border-t border-gray-100 pt-8 text-center md:flex-row font-body">
                    <p className="text-sm text-black/40">
                        © {new Date().getFullYear()} Dr. Ajinkya's Dento Care. All rights reserved.
                    </p>
                    <nav className="flex gap-6 text-sm">
                        <a href="#" className="text-black/40 transition-colors hover:text-black">
                            Privacy Policy
                        </a>
                        <a href="#" className="text-black/40 transition-colors hover:text-black">
                            Terms of Service
                        </a>
                    </nav>
                </div>
            </div>
        </footer>
    )
}

export { DentoFooter }
